import React from 'react';
import { ChatLanguage, ChatStrings } from '../../i18n';
import { closeEmbeddedChat, isEmbedded } from '../../../../embed';
import { ChatTheme } from '../../../../theme';
import { CloseIcon, MoonIcon, SunIcon } from '../Icons';

interface ChatHeaderProps {
   /** The hotel's name when the API sends it; "Concierge" until then. */
   hotelName?: string;
   /** The cover has scrolled under the capsule (or there is no cover): the capsule carries the name. */
   condensed: boolean;
   language: ChatLanguage;
   onLanguageChange: (language: ChatLanguage) => void;
   theme: ChatTheme;
   onToggleTheme: () => void;
   strings: ChatStrings;
}

const PUBLIC_URL = process.env.PUBLIC_URL ?? '';

/**
 * A capsule floating over the canvas: the XenioAI mark, the hotel's name once its cover has scrolled under,
 * and the theme, language and (in the widget) close circles. It holds still while the column moves.
 */
const ChatHeader: React.FC<ChatHeaderProps> = ({ hotelName, condensed, language, onLanguageChange, theme, onToggleTheme, strings }) => {
   const nextLanguage: ChatLanguage = language === 'el' ? 'en' : 'el';
   const nextTheme: ChatTheme = theme === 'dark' ? 'light' : 'dark';

   return (
      <header className={`cx-header${condensed ? ' cx-header--condensed' : ''}`}>
         <div className="cx-measure">
            <div className="cx-capsule cx-frosted cx-frosted--thick">
               <div className="cx-header__row">
                  <picture className="cx-logo">
                     <source srcSet={`${PUBLIC_URL}/brand/xenioai-logo-dark.png`} media={theme === 'dark' ? 'all' : 'not all'} />
                     <img src={`${PUBLIC_URL}/brand/xenioai-logo-light.png`} alt={strings.logoAlt} width={40} height={40} decoding="async" />
                  </picture>
                  <div className="cx-header__identity" aria-hidden={!condensed}>
                     <p className="cx-header__title" dir="auto">
                        {hotelName ?? strings.conciergeTitle}
                     </p>
                     <p className="cx-header__sub">{hotelName ? strings.headerSub : strings.byXenioAI}</p>
                  </div>
                  <div className="cx-header__actions">
                     <button
                        type="button"
                        className="cx-icon-btn"
                        onClick={onToggleTheme}
                        aria-label={strings.themeToggleLabel(nextTheme)}
                        title={strings.themeToggleLabel(nextTheme)}
                     >
                        {theme === 'dark' ? <SunIcon /> : <MoonIcon />}
                     </button>
                     <button
                        type="button"
                        className="cx-icon-btn"
                        onClick={() => onLanguageChange(nextLanguage)}
                        aria-label={strings.languageToggleLabel(nextLanguage)}
                        title={strings.languageToggleLabel(nextLanguage)}
                     >
                        <span aria-hidden="true">{strings.languageCode}</span>
                     </button>
                     {isEmbedded && (
                        <button type="button" className="cx-icon-btn" onClick={closeEmbeddedChat} aria-label={strings.closeChat} title={strings.closeChat}>
                           <CloseIcon />
                        </button>
                     )}
                  </div>
               </div>
            </div>
         </div>
      </header>
   );
};

export default ChatHeader;
